import I18n from 'I18n';
import { fromJS, Map as ImmutableMap } from 'immutable';
import { useEffect, useMemo, useState } from 'react';
import { Button } from 'foundations-components/transitional/components/Button';
import { H7 } from 'foundations-components/transitional/components/H7';
import { Small } from 'foundations-components/transitional/components/Small';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';

// @ts-expect-error untyped import
import FilterOperatorDateInput from 'customer-data-filters/components/operator/FilterOperatorDateInput';
// @ts-expect-error untyped import
import FilterOperatorRefinableInput from 'customer-data-filters/components/operator/FilterOperatorRefinableInput';
import { fromObjectSegFilterStrict } from 'customer-data-filters/converters/objectSeg/fromObjectSegFilter';
import { toObjectSegFilterStrict } from 'customer-data-filters/converters/objectSeg/toObjectSegFilter';
import FilterOperatorErrorRecord from 'customer-data-filters/filterQueryFormat/FilterOperatorErrorRecord';
import { getEventDateFilterProperty } from './event-date-filter-property';
// @ts-expect-error untyped import
import { getOperators } from '../../event-date-interval-utils';
// @ts-expect-error untyped import
import { useTableDescription, useTableSource } from '../SourceMeta';
import { findEventLikeTable, getDataSourceId, getTableLabel } from '../../../../utils/table-utils';
import { getLabelString, getOperatorLabel, getPlaceholder, SNOWFLAKE_FILTER_FAMILY, toDSFieldRecord
// @ts-expect-error untyped import
} from '../../xo-filter-utils';
import { getIsUngated, useFilterRelevantUserInfo } from '../../../../../shared/lib/utils';
import { useSnowflakeSourceData } from '../../../../metadata/sources';
import { Metrics } from '../../../../../shared/Metrics';
import { REPORTING_LEGACY_OPERATOR_USAGE } from 'reporting-data/constants/trackingMetrics';
import { useCheckEventDateLimit } from './use-check-event-date-limit';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const NO_ERROR = FilterOperatorErrorRecord({
  error: false
});
const useEventTable = () => {
  const tableDescription = useTableDescription();
  const {
    data: {
      snowflakeTables
    } = {},
    loading
  } = useSnowflakeSourceData();
  if (tableDescription == null) {
    throw new Error('EventDateIntervalFilter can only be rendered when there is a table description!');
  }
  const maybeEventLikeTable = findEventLikeTable(tableDescription, snowflakeTables || ImmutableMap());
  return {
    loading,
    maybeEventLikeTable
  };
};
const EventDateFilterPanel = ({
  filter,
  onDone,
  onCancel
}) => {
  const [draftFilter, setDraft] = useState(filter);
  const {
    userGates
  } = useFilterRelevantUserInfo();
  const {
    maybeEventLikeTable: eventTableDescription,
    loading
  } = useEventTable();
  const {
    name: tableName
  } = eventTableDescription || {
    name: ''
  };
  const dataSourceId = eventTableDescription && !loading ? getDataSourceId(eventTableDescription) : undefined;
  const source = useTableSource(tableName);
  const tableLabel = getTableLabel(tableName, source);
  const mappedProps = useMemo(() => {
    const {
      filter: propertyFilter
    } = draftFilter;
    const field = toDSFieldRecord(getEventDateFilterProperty());
    const operator = fromObjectSegFilterStrict(propertyFilter.toJS(), () => field, SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true);
    return {
      currencyCode: 'USD',
      error: NO_ERROR,
      showIncludeUnknownValues: false,
      isRollingDateOffsetInputEnabled: false,
      isXoEnabled: true,
      getOperatorLabel,
      getIsUngated: gate => getIsUngated(gate, userGates),
      getDefaultTimezone: () => I18n.moment.portalTz().tz(),
      getReferencedObjectType: () => undefined,
      getSpecialOptionsForReferenceType: () => undefined,
      getFamilyValueResolver: () => undefined,
      getInputComponent: () => FilterOperatorDateInput,
      getLabelString: () => getLabelString(field),
      getPlaceholder: () => getPlaceholder(operator),
      getOperators,
      value: operator
    };
  }, [draftFilter, userGates]);
  const handleChange = ({
    target: {
      value: operator
    }
  }) => {
    const nextObjectSegFilter = toObjectSegFilterStrict(operator, SNOWFLAKE_FILTER_FAMILY, gate => getIsUngated(gate, userGates), true);
    const nextFilter = filter.set('filter', fromJS(nextObjectSegFilter));
    setDraft(nextFilter);
  };
  const handleDone = () => {
    onDone({
      draftFilter
    });
  };
  const {
    disabled,
    error,
    message: maybeDisabledMessage,
    maxDays
  } = useCheckEventDateLimit(draftFilter, tableLabel, dataSourceId);
  useEffect(() => {
    Metrics.counter(REPORTING_LEGACY_OPERATOR_USAGE, {
      propertyType: 'event-date'
    }).increment();
  }, []);
  if (loading) {
    return null;
  }
  if (!loading && !eventTableDescription) {
    throw new Error('EventDateIntervalFilter can only be rendered when there is an event-like table in the report!');
  }
  if (error) {
    console.error('Could not retrieve event date limit');
  }
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(H7, {
      children: getEventDateFilterProperty().label
    }), /*#__PURE__*/_jsx(FilterOperatorRefinableInput, Object.assign({}, mappedProps, {
      onChange: handleChange
    })), /*#__PURE__*/_jsx(Small, {
      children: I18n.text('reporting-snowflake.filter-editor.event-date-filter.max-event-interval-msg', {
        maxDays,
        tableLabel
      })
    }), /*#__PURE__*/_jsx(TFlex, {
      justify: "between",
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsxs("div", {
        children: [/*#__PURE__*/_jsx(Tooltip, {
          disabled: !maybeDisabledMessage,
          title: maybeDisabledMessage,
          children: /*#__PURE__*/_jsx(Button, {
            use: "tertiary",
            size: "xs",
            onClick: handleDone,
            disabled: disabled,
            children: I18n.text('reporting-snowflake.filter-editor.editor-panel.apply')
          })
        }), /*#__PURE__*/_jsx(Button, {
          use: "tertiary-light",
          size: "xs",
          onClick: onCancel,
          children: I18n.text('reporting-snowflake.filter-editor.editor-panel.cancel')
        })]
      })
    })]
  });
};
export default EventDateFilterPanel;